import React, { useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  Grid, MenuItem, Select,
  Step,
  StepLabel,
  Stepper,
  Typography } from
"@mui/material";
import Button from "@mui/material/Button";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Alert } from "@mui/lab";
import { useQueryClient } from "react-query";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { LoginSocialFacebook } from "reactjs-social-login";
import { useTranslation } from "react-i18next";
import { MixpanelService } from "crqlar-react-common/dist/services/analytics/mixpanel.service";
import { FacebookConfig } from "crqlar-react-common/dist/models/meta/facebookConfig";
import { useFacebookConfig } from "crqlar-react-common/dist/utils/hooks/meta/useFacebookConfig";
import { FacebookService } from "crqlar-react-common/dist/services/meta/facebook.service";
import { useReadOnly } from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";

interface Props {
  handleBack: () => void;
  activeStep: number;
}

const AccountSetup = (props: Props) => {
  const [isSaving, setIsSaving] = React.useState(false);
  const [authorizationDone, setAuthorizationDone] = React.useState(false);
  const [selectedAdAccountId, setSelectedAdAccountId] = useState<string | null>(null);
  const [adAccounts, setAdAccounts] = useState<any[]>([]);
  const [isLoadingAdAccounts, setIsLoadingAdAccounts] = useState(false);
  const [isErrorAdAccounts, setIsErrorAdAccounts] = useState(false);
  const { t } = useTranslation();
  // Read-only users may not connect a Meta ad account.
  const readOnly = useReadOnly();

  const facebookConfig = useFacebookConfig();

  const queryClient = useQueryClient();
  const handleFinishFacebookSetup = async () => {
    setIsSaving(true);
    const newConfig: FacebookConfig = {
      ...facebookConfig,
      appId: facebookConfig?.appId,
      appSecret: facebookConfig?.appSecret,
      adAccountId: selectedAdAccountId as string
    };
    FacebookService.saveConfig(newConfig).then((response) => {
      queryClient.invalidateQueries(['facebookConfig']);
    });
  };

  useEffect(() => {
    if (facebookConfig?.accessToken) {
      fetchAdAccounts();
    }
  }, [facebookConfig]);

  const fetchAdAccounts = async () => {
    if (!facebookConfig?.accessToken) {
      return [];
    }
    setIsLoadingAdAccounts(true);
    setIsErrorAdAccounts(false);
    try {
      const accounts = await FacebookService.getAdAccounts();
      setAdAccounts(accounts);
      const firstAccount = accounts[0];
      setSelectedAdAccountId(firstAccount.id);
    } catch (error) {
      setIsErrorAdAccounts(true);
    } finally {
      setIsLoadingAdAccounts(false);
    }
  };

  const handleSuccessAuthorize = (response: any) => {
    setAuthorizationDone(true);
    const newConfig: FacebookConfig = {
      ...facebookConfig,
      accessToken: response?.data?.accessToken,
      appId: facebookConfig?.appId,
      appSecret: facebookConfig?.appSecret,
      enabled: true
    };
    FacebookService.saveConfig(newConfig).then((response) => {
      queryClient.invalidateQueries(['facebookConfig']);
    });
    MixpanelService.getInstance().track('connect meta');
  };

  const handleLoginFail = (response: any) => {
    setAuthorizationDone(false);
    setIsErrorAdAccounts(true);
  };

  const handleAdAccountChange = (newValue: string) => {
    setSelectedAdAccountId(newValue);
  };

  const handleChangeFbUser = () => {
    FacebookService.changeFacebookUser().then(() => {
      queryClient.invalidateQueries(['facebookConfig']);
    });
  };

  return (
    <Box sx={{ p: 4, maxWidth: '1200px', margin: 'auto' }}>
            <Typography variant="h4" gutterBottom sx={{ mb: 4 }}>
                {t('Ad Account Setup')}
            </Typography>
            <Stepper activeStep={props.activeStep} sx={{ mb: 4 }}>
                <Step>
                    <StepLabel>{t('General')}</StepLabel>
                </Step>
                <Step>
                    <StepLabel>{t('Account Setup')}</StepLabel>
                </Step>
            </Stepper>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="h4" gutterBottom>
                        {t("Meta (Facebook / Instagram) Ads")}
                    </Typography>

                    {
          facebookConfig?.accessToken &&
          <Typography variant="h4" sx={{ textAlign: 'right' }}>
                            {t('Choose Ad Account')}
                        </Typography>
          }
                </Box>

                <Grid container
        sx={{
          mb: 4
        }}>

                    <Grid item md={6}>

                        {
            facebookConfig?.appId ?

            <LoginSocialFacebook
              appId={facebookConfig.appId as string}
              scope="ads_management,business_management"
              onResolve={handleSuccessAuthorize}
              onLogoutSuccess={handleChangeFbUser}
              onReject={(error: any) => {
                handleLoginFail(error);
              }}>

                                        <Button
                variant="contained"
                color="primary"
                disabled={readOnly}
                sx={{ backgroundColor: '#21cbf5', mb: 3 }}>

                                            {t('Connect Ad Account with Crqlar')}
                                        </Button>
                                    </LoginSocialFacebook> :


            // If app id is not defined - display an error
            <Alert severity="error" sx={{ width: '50%' }}>
                                    <Typography variant="h6">{t('Meta app id missing!')}</Typography>
                                </Alert>

            }

                    </Grid>

                    {!facebookConfig?.accessToken &&
          <Grid item md={6}>
                            <Typography variant="body1" sx={{ lineHeight: '1.5', mb: 2 }}>
                                {t('Account setup message')}
                            </Typography>
                        </Grid>
          }

                    {
          facebookConfig?.accessToken &&
          <Grid item md={6} sx={{ textAlign: 'right', mb: 2 }}>
                            <Box>
                                <Box sx={{ display: 'flex', flexDirection: 'row', gap: 3, justifyContent: 'flex-end' }}>
                                    {isLoadingAdAccounts &&
                <Box sx={{ ml: 'auto' }}>
                                            <CircularProgress />
                                        </Box>
                }
                                </Box>
                                {!isLoadingAdAccounts && !isErrorAdAccounts &&
              <Select
                value={selectedAdAccountId}
                onChange={(e) => handleAdAccountChange(e.target.value as string)}
                displayEmpty
                sx={{ minWidth: 400 }}>

                                        {adAccounts.map((adAccount: any) =>
                <MenuItem key={adAccount.id} value={adAccount.id}>
                                                <Box>
                                                    <Typography variant="h6">{adAccount.name}</Typography>
                                                    <Typography variant="body2">{adAccount.id}</Typography>
                                                </Box>
                                            </MenuItem>
                )}
                                    </Select>
              }
                            </Box>
                        </Grid>
          }

                    {
          facebookConfig?.accessToken && !isErrorAdAccounts &&
          <Grid item md={6}>
                            <Alert severity="success" sx={{ width: '50%' }}>
                                <Typography variant="h6">{t('Authorization successful!')}</Typography>
                            </Alert>
                        </Grid>
          }

                    {
          isErrorAdAccounts &&
          <Grid item md={6}>
                                <Alert severity="error" sx={{ width: '50%' }}>
                                    <Typography variant="h6">{t('Facebook Login Failed')}</Typography>
                                </Alert>
                            </Grid>

          }

                    {
          !facebookConfig?.accessToken && !isErrorAdAccounts &&
          <Grid item md={6}></Grid>
          }

                    {
          !facebookConfig?.accessToken &&
          <Grid item md={6}>
                            <Typography variant={'body1'} sx={{ lineHeight: '1.5' }}>
                                {t('FB Access token message')}
                            </Typography>
                        </Grid>
          }

                </Grid>

            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Button
          variant="text"
          startIcon={<ArrowBackIcon />}
          onClick={props.handleBack}>

                    {t('Back')}
                </Button>
                <Button
          variant="contained"
          color="primary"
          sx={{ backgroundColor: '#21cbf5' }}
          onClick={handleFinishFacebookSetup}
          endIcon={<ArrowForwardIcon />}
          disabled={!selectedAdAccountId || isErrorAdAccounts}>

                    {
          isSaving ? <>
                            <CircularProgress size={15} sx={{ marginRight: '10px' }}></CircularProgress>
                            {t('Finish setup')}
                        </> : t('Finish setup')
          }
                </Button>
            </Box>
        </Box>);

};

export default AccountSetup;
